/**
 * MUI HeatMap Component
 * 
 * This component is the Material UI equivalent of the original HeatMap component.
 * It renders a heatmap visualization on a canvas element for the Master Designer application.
 */

import React, { memo, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import Canvas from '../atoms/Canvas';
import { getScale } from 'reduxModules/ducks/ui';
import { isNull, round } from 'lodash';
import { getPlotProps } from 'reduxModules/selectors/containerSelectors';
import { HeatMapProps } from 'types/components/molecules';
import { PlotProps } from 'types/redux/container';
import { gradients } from 'utilities/color';
import { has } from 'lodash';

/**
 * Interface for subdivision with optional type
 */
interface Subdivision {
  left: number;
  top: number;
  length: number;
  width: number;
  type?: string | null;
}

/**
 * Interface for point coordinates
 */
interface Point {
  x: number;
  y: number;
  px: number;
  py: number;
}

/**
 * MUI HeatMap component
 * 
 * @param {Object} props - Component props
 * @param {string} props.id - Unique identifier for the heatmap
 * @param {Object} props.anchor - Position and dimensions of the heatmap anchor
 * @param {Object} props.subdivision - subdivision information for positioning the heatmap
 * @param {Object} props.data - Data for generating the heatmap
 * @param {number[][]} props.data.mapArray - 2D array of heatmap values
 * @param {number} props.data.receiverSpacing - Spacing between data points
 * @param {number} props.data.receiverXOffset - X-axis offset for data points
 * @param {number} props.data.receiverYOffset - Y-axis offset for data points
 * @returns {JSX.Element} The MUI HeatMap component
 */
const MuiHeatMap: React.FC<HeatMapProps> = memo(({ id, anchor, subdivision, data }) => {
  const scale = useSelector(getScale);
  const plotProps = useSelector(getPlotProps) as PlotProps;
  const { minLevel = 0, maxLevel = 100, accuracy = 100, pointToPixelValue = null } = plotProps || {};
  const muiTheme = useMuiTheme();

  // Generate gradient based on current theme mode
  const gradientRGBA = useMemo(() => {
    const { plotType: type, ...properties } = plotProps as any;
    if (has(gradients, type)) {
      const themeMode = muiTheme.palette.mode;

      // For camera plots, pass coverageRadius, limitRadius, and isActive from data
      const extraProps = type === 'cameraCoverage' && data?.camera
        ? {
            coverageRadius: data.camera.coverageRadius,
            limitRadius: data.camera.limitRadius,
            isActive: data.isActive !== undefined ? data.isActive : true // Default to active for single-imager cameras
          }
        : {};

      return (gradients as any)[type]({ ...properties, themeMode, ...extraProps });
    }
    return undefined;
  }, [plotProps, muiTheme.palette.mode, data]);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  /**
   * Calculate canvas properties based on anchor and subdivision
   * The anchor is already constrained by HeatMapManager, so we just apply subdivision positioning
   */
  const finalTop = anchor.top + subdivision.top * scale;
  const finalLeft = anchor.left + subdivision.left * scale;
  const finalWidth = (subdivision.length * scale) || anchor.width;
  const finalHeight = (subdivision.width * scale) || anchor.height;

  /**
   * Effect to draw the heatmap on the canvas
   */
  useEffect(() => {
    // Check if canvas ref exists and is actually a canvas element
    if (!canvasRef.current || !(canvasRef.current instanceof HTMLCanvasElement)) return;
    if (!gradientRGBA || !data.mapArray) return; // Early return if no data or gradient
    
    // Use Math.round to ensure integer pixel dimensions
    const canvasWidth = Math.round(finalWidth);
    const canvasHeight = Math.round(finalHeight);

    // Set canvas internal resolution to match display size
    canvasRef.current.width = canvasWidth;
    canvasRef.current.height = canvasHeight;

    const ctx = canvasRef.current.getContext('2d');

    if (!ctx) return; // Early return if context is not available

    const heatmapimage = ctx.createImageData(canvasWidth, canvasHeight);
    const heatmapdata = heatmapimage.data;
    const plot = Array(canvasWidth * canvasHeight).fill(0);

    for (let i = 0; i < plot.length; i++) {
      const y = Math.floor(i / canvasWidth);
      const x = i - y * canvasWidth;
      const dx = x / scale + subdivision.left;
      const dy = y / scale + subdivision.top;

      // Use safe type assertion for pointToPixelValue since we don't have its exact type
      const pointToPixelValueFn = pointToPixelValue as (point: Point, data: any) => number;

      let pixelLevel = pointToPixelValueFn({ x: dx, y: dy, px: x, py: y }, data);

      // Skip pixels that are outside coverage area (Infinity) or invalid (NaN)
      if (!isFinite(pixelLevel)) {
        continue;
      }
      
      pixelLevel = Math.max(minLevel, Math.min(maxLevel, pixelLevel));
      
      const index = round(pixelLevel * (accuracy as number));
      const rgb = gradientRGBA[index];
      const baseindex = 4 * i;
      
      if (!rgb || rgb.length < 3) continue;
      
      // Apply custom opacity if provided in data
      const baseOpacity = rgb[3] ?? 1;
      const finalOpacity = data.opacity !== undefined ? data.opacity * baseOpacity : baseOpacity;
      
      heatmapdata[baseindex] = rgb[0]; // red
      heatmapdata[baseindex + 1] = rgb[1]; // green
      heatmapdata[baseindex + 2] = rgb[2]; // blue
      heatmapdata[baseindex + 3] = finalOpacity * 255; // alpha with custom opacity
    }

    ctx.putImageData(heatmapimage, 0, 0);
  }, [
    anchor.left, 
    anchor.top, 
    anchor.width, 
    anchor.height,
    subdivision.left, 
    subdivision.top, 
    subdivision.length, 
    subdivision.width,
    subdivision.type,
    scale, 
    minLevel, 
    maxLevel, 
    accuracy, 
    pointToPixelValue, 
    gradientRGBA,
    data.mapArray,
    data.receiverSpacing,
    data.receiverXOffset,
    data.receiverYOffset,
    data.opacity
  ]);

  const props = {
    id: id,
    top: finalTop,
    left: finalLeft,
    width: Math.max(finalWidth, 1),
    height: Math.max(finalHeight, 1)
  };

  return <Canvas ref={canvasRef} {...props}></Canvas>;
});

MuiHeatMap.displayName = 'MuiHeatMap';
export default MuiHeatMap;
